import {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useMemo,
  Fragment,
} from "react";
// import { useBottomScrollListener } from "react-bottom-scroll-listener";
import Loader from "../Loader/Loader.jsx";
import { compareRows } from "./tableSort.js";
import {
  columnLabel,
  moveColumn as moveColumnIn,
  resolveColumnState,
  visibleColumns as resolveVisibleColumns,
} from "./columnVisibility.js";
import "./table.scss";

export default forwardRef(function Table(
  {
    data,
    fetchFn,
    config,
    filter,
    loading,
    emptyMessage = "No items to show",
    paginationCountText = "Elements: ",
    className,
    enableColumnOrdering = true,
    rowClassName,
    rowIcon,
    hideIcon,
    defaultSort,
    sortDirection: controlledSortDirection,
    onSortChange,
    showFooter = false,
    onRowOrderChange,
    draggableRows = false,
    allowSubrows,

    // Visibility
    visibilityKey,
    visibilityContext,
    navigate,
  },
  ref,
) {
  if (visibilityKey && !visibilityContext) {
    throw new Error(
      "visibilityKey provided without visibilityContext. Column visibility will not work.",
    );
  }
  const [sortKey, setSortKey] = useState(defaultSort);
  const [sortDirection, setSortDirection] = useState(
    controlledSortDirection || "desc",
  );
  const [customSortFunction] = useState(null);
  const [dragStartIndex, setDragStartIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  // const [scroll, setScroll] = useState();
  const [list, setList] = useState(data || []);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 40;
  const [draggedRowIndex, setDraggedRowIndex] = useState(null);
  const { visibilityMap, onVisibilityChange, searchMap, setSearchField } =
    visibilityContext || {};

  // useBottomScrollListener(() => {
  //   if (window.pageYOffset !== scroll) {
  //     setScroll(window.pageYOffset);
  //     if (!data) loadMore();
  //   }
  // });

  // Which columns are shown, and in what order — derived straight from the stored map rather than
  // mirrored into state. There is nothing to keep in step this way: the `ColumnsButton` that owns
  // the checkboxes writes the same map, and the table re-renders because the map changed. (It also
  // means the first render already draws the real columns; the mirror-into-state version rendered
  // one commit with no columns at all, which every test had to wait out.)
  const columnVisibility = useMemo(
    () =>
      resolveColumnState(
        config.columns,
        visibilityKey ? visibilityMap?.[visibilityKey] : null,
      ),
    [config.columns, visibilityKey, visibilityMap],
  );

  const fetchData = async (currentPage) => {
    let result = await fetchFn({
      pageSize,
      currentPage,
      sortKey,
      sortDirection,
      filter,
    });

    if (result.status !== 200) return;

    setList((list) => list.concat(result.data));

    if (result.data.length === pageSize) {
      setShowLoadMore(true);
    } else {
      setShowLoadMore(false);
    }
  };

  const loadMore = () => {
    setCurrentPage(currentPage + 1);
    fetchData(currentPage + 1);
  };

  useEffect(() => {
    if (data) {
      setList(data);
    } else {
      setList([]);
    }

    setCurrentPage(0);
    if (fetchFn) fetchData(0, []);
  }, [sortKey, sortDirection, filter, data]);

  function getNestedValue(obj, path) {
    return path.split(".").reduce((o, k) => (o ? o[k] : undefined), obj);
  }

  let filteredData = list.filter((item, key) => {
    item.index = key;
    if (fetchFn) return true;
    for (let filterKey in filter) {
      if (filter[filterKey] === "true" || filter[filterKey] === "false") {
        if (!item[filterKey]) item[filterKey] = false;
        if (
          filter[filterKey] &&
          item[filterKey].toString() !== filter[filterKey].toString()
        ) {
          return false;
        }
      } else {
        if (!filter[filterKey] && filter[filterKey] !== 0) continue;
        const value = filterKey.includes(".")
          ? getNestedValue(item, filterKey)
          : item[filterKey];
        const filterValue = filter[filterKey];

        // null/undefined
        if (value === undefined || value === null) return false;

        // String
        if (typeof value === "string") {
          if (
            !value.toLowerCase().includes(String(filterValue).toLowerCase())
          ) {
            return false;
          }
        }
        // Number or boolean
        else if (typeof value === "number" || typeof value === "boolean") {
          if (value !== filterValue) {
            return false;
          }
        }
        // Fallback
        else if (value !== filterValue) {
          return false;
        }
      }
    }
    return true;
  });

  useImperativeHandle(ref, () => {
    return {
      filteredData,
      columnVisibility,
    };
  }, [filteredData, columnVisibility]);

  useEffect(() => {
    if (defaultSort !== undefined) {
      if (defaultSort !== sortKey) {
        setSortKey(defaultSort);
      }
    } else if (searchMap?.[visibilityKey]?.sortKey) {
      setSortKey(searchMap[visibilityKey].sortKey);
    }
  }, [defaultSort]);

  useEffect(() => {
    if (controlledSortDirection !== undefined) {
      if (controlledSortDirection !== sortDirection) {
        setSortDirection(controlledSortDirection);
      }
    } else if (searchMap?.[visibilityKey]?.sortDirection) {
      setSortDirection(searchMap[visibilityKey].sortDirection);
    }
  }, [controlledSortDirection]);

  // Update sort and notify parent
  const setSortKeyFn = (key, sortOverride) => {
    let direction = sortDirection;
    if (sortKey === key) {
      direction = sortDirection === "asc" ? "desc" : "asc";
    } else {
      direction = sortOverride || "asc";
    }
    setSortKey(key);
    setSortDirection(direction);
    if (onSortChange) onSortChange(key, direction);

    if (visibilityKey) {
      if (sortKey === key) {
        setSearchField(visibilityKey, "sortDirection", direction);
      } else {
        setSearchField(visibilityKey, "sortKey", key);
        setSearchField(visibilityKey, "sortDirection", direction);
      }
    }
  };

  // The active column's own sort accessor, resolved once per sort rather than once per comparison.
  // Only a column whose `key` IS the sort key can supply one — that is the key a header click sets.
  const activeSortValue = useMemo(
    () => config.columns.find((col) => col.key === sortKey)?.sortValue,
    [config.columns, sortKey],
  );

  const sortFn = (a, b) =>
    compareRows(a, b, {
      sortKey,
      sortDirection,
      customSortFunction,
      sortValue: activeSortValue,
    });

  const visibleColumns = resolveVisibleColumns(
    config.columns,
    columnVisibility,
  );

  const handleDragStart = (e, index) => {
    if (!enableColumnOrdering) return;
    if (!visibilityKey) return;
    setDragStartIndex(index);
    e.dataTransfer.setData("text/plain", index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    if (!enableColumnOrdering) return;
    if (!visibilityKey) return;
    e.preventDefault();
    setDragOverIndex(index);
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    if (!enableColumnOrdering) return;
    if (!visibilityKey) return;
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
    setDragOverIndex(null);
    if (dragIndex === dropIndex) return;
    moveColumn(dragIndex, dropIndex);
  };

  const moveColumn = (fromIndex, toIndex) => {
    // fromIndex/toIndex index the VISIBLE columns; `moveColumn` in columnVisibility.js translates
    // that into the full column order (hidden ones included) and renumbers everything.
    const movedKey = visibleColumns[fromIndex]?.key;
    const targetKey = visibleColumns[toIndex]?.key;
    if (movedKey === undefined || targetKey === undefined) return;

    const updatedPositions = moveColumnIn(
      config.columns,
      columnVisibility,
      movedKey,
      targetKey,
    );
    if (updatedPositions) onVisibilityChange(visibilityKey, updatedPositions);
  };

  const headerRender = (
    <tr>
      {/* The row-icon column. It used to double as the show/hide-columns menu — a bare `···` no
          one found — which now lives in the page toolbar as `ColumnsButton`, the same control in
          every app (PDS change request 10gx8). */}
      {!hideIcon && <th className="headerFilter" />}

      {allowSubrows && (
        <th>
          <div className="col-smaller"></div>
        </th>
      )}
      {visibleColumns.map((col, index) => {
        const isSorted =
          sortKey &&
          ((col.sort && sortKey === col.sort) || sortKey === col.key);
        let sortIcon = null;
        if (isSorted) {
          sortIcon = (
            <i
              key={index}
              className="material-icons sort-icon"
              style={{ fontSize: "16px", verticalAlign: "middle" }}
            >
              {sortDirection === "asc" ? "arrow_upward" : "arrow_downward"}
            </i>
          );
        }

        return (
          <th
            key={col.key}
            draggable={enableColumnOrdering && visibilityKey !== undefined}
            onDragStart={
              enableColumnOrdering
                ? (e) => handleDragStart(e, index)
                : undefined
            }
            onDragOver={
              enableColumnOrdering ? (e) => handleDragOver(e, index) : undefined
            }
            onDrop={
              enableColumnOrdering ? (e) => handleDrop(e, index) : undefined
            }
            onClick={() => setSortKeyFn(col.key, col.sort)}
            className={`
      ${col.sort || col.key ? "sortable" : ""}
      ${isSorted ? "sort " + sortDirection : ""}
      ${col.className || ""}
      ${dragOverIndex === index ? (dragStartIndex > index ? "th-drag-over-left" : "th-drag-over-right") : ""}
    `}
            style={{ cursor: "pointer" }}
          >
            {columnLabel(col)} {sortIcon}
          </th>
        );
      })}
    </tr>
  );

  const renderRow = (row, indexRow, subIndex) => {
    let defaultClasses = config.linkRow || config.onClickRow ? "pointer" : "";
    const extraClasses =
      typeof rowClassName === "function" ? rowClassName(row, indexRow) : "";
    const combinedClasses = `${defaultClasses} ${extraClasses}`.trim();

    let icon = typeof rowIcon === "function" ? rowIcon(row, indexRow) : rowIcon;

    const toggleSubRow = () => {
      const newData = [...filteredData];
      newData[indexRow].showSubrows = !newData[indexRow].showSubrows;
      setList(newData);
    };

    return (
      <Fragment key={`row_${indexRow}`}>
        <tr
          key={`row_${indexRow}`}
          className={combinedClasses}
          onClick={() => {
            if (!row.subrows) {
              onClickRow(row, row.index, filteredData, indexRow, subIndex);
            } else {
              toggleSubRow();
            }
          }}
          draggable={!!onRowOrderChange && draggableRows}
          onDragStart={
            !!onRowOrderChange && draggableRows
              ? () => setDraggedRowIndex(indexRow)
              : undefined
          }
          onDragOver={
            !!onRowOrderChange && draggableRows
              ? (e) => e.preventDefault()
              : undefined
          }
          onDrop={
            !!onRowOrderChange && draggableRows
              ? () => {
                  if (draggedRowIndex === null || draggedRowIndex === indexRow)
                    return;
                  const newOrder = [...filteredData];
                  const [removed] = newOrder.splice(draggedRowIndex, 1);
                  newOrder.splice(indexRow, 0, removed);
                  setDraggedRowIndex(null);
                  onRowOrderChange(newOrder);
                }
              : undefined
          }
          style={
            !!onRowOrderChange && draggableRows
              ? { cursor: "ns-resize" }
              : undefined
          }
        >
          {!hideIcon && (
            <td className="table-icon">
              {icon ? (
                <i className="material-symbols-outlined">{icon}</i>
              ) : null}
            </td>
          )}
          {allowSubrows && (
            <td>
              <div className="col-smaller">
                {row.subrows && row.subrows.length > 0 && (
                  <button className="square-btn small transparent">
                    <i className="material-icons">
                      {row.showSubrows ? "expand_less" : "expand_more"}
                    </i>
                  </button>
                )}
              </div>
            </td>
          )}
          {visibleColumns.map((col, indexCol) => (
            <td
              key={`row_content_${indexRow}_${indexCol}`}
              className={col.className || ""}
            >
              {col.template
                ? col.template(row, indexRow, subIndex)
                : row[col.key]}
            </td>
          ))}
        </tr>
        {row.showSubrows &&
          row.subrows &&
          row.subrows.length > 0 &&
          row.subrows.map((subrow, subIndex) =>
            renderRow(subrow, indexRow, subIndex),
          )}
      </Fragment>
    );
  };

  const rowsRender = filteredData.sort(sortFn).map((row, indexRow) => {
    return renderRow(row, indexRow);
  });

  const onClickRow = (row, indexRow, filteredList, filteredIndex, subIndex) => {
    if (config.onClickRow) {
      config.onClickRow(row, indexRow, filteredList, filteredIndex, subIndex);
    } else if (config.linkRow) {
      navigate(
        config.linkRow(row, indexRow, filteredList, filteredIndex, subIndex),
      );
    }
  };

  return (
    <>
      <div className="tw-table-inner">
        <table className={className}>
          <thead>{headerRender}</thead>
          <tbody>
            {loading ? (
              <tr className="loader-container">
                <td colSpan={visibleColumns.length + (hideIcon ? 0 : 1)}>
                  <Loader />
                </td>
              </tr>
            ) : filteredData.length > 0 ? (
              rowsRender
            ) : (
              emptyMessage && (
                <tr className="empty-row">
                  <td
                    colSpan={visibleColumns.length + (hideIcon ? 0 : 1)}
                    style={{ textAlign: "left" }}
                  >
                    {emptyMessage}
                  </td>
                </tr>
              )
            )}
          </tbody>
          {showLoadMore && (
            <tfoot onClick={loadMore}>
              <tr>
                <td
                  colSpan={visibleColumns.length + (hideIcon ? 0 : 1)}
                  style={{ cursor: "pointer" }}
                >
                  Load more...
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {showFooter && (
        <div className="table-footer-sticky">
          {paginationCountText} {filteredData.length}
        </div>
      )}
    </>
  );
});
